import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';

/** Lightweight toast component */
function Toast({ notice, onClose }) {
  // notice: { type: 'success'|'error'|'info', message: string }
  useEffect(() => {
    if (!notice) return;
    const id = setTimeout(onClose, 3000);
    return () => clearTimeout(id);
  }, [notice, onClose]);

  if (!notice) return null;
  const palette = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-gray-800',
  }[notice.type || 'info'];

  return (
    <div
      className={`fixed bottom-4 right-4 z-[1000] text-white px-4 py-3 rounded shadow-lg ${palette}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span>{notice.message}</span>
        <button
          onClick={onClose}
          aria-label="Close notification"
          className="ml-2 text-white/80 hover:text-white"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function RichTextEditor({ item, initialContent, siteContentId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageData, setImageData] = useState({ displayText: '', imageUrl: '', key: '' });
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkData, setLinkData] = useState({ displayText: '', targetUrl: '' });
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileData, setFileData] = useState({ displayText: '', fileUrl: '' });
  const [toast, setToast] = useState(null);
  const contentRef = useRef(null);

  const showToast = (type, message) => setToast({ type, message });

  useEffect(() => {
    const handleSelectionChange = () => {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    contentRef.current.innerHTML = initialContent || '';
  }, [initialContent]);

  async function getDownloadUrlForPrivate(key) {
    const r = await api.post('/media/download-url', { key });
    return r.data.url;
  }

  function insertHtmlAtCursor(html) {
    const host = contentRef.current;
    if (!host) return;
    host.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      const div = document.createElement('div');
      div.innerHTML = html;
      host.appendChild(div);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const frag = range.createContextualFragment(html);
    range.insertNode(frag);
  }

  const handleFontFamilyChange = (e) => { document.execCommand('fontName', false, e.target.value); contentRef.current?.focus(); };
  const handleFontSizeChange   = (e) => { document.execCommand('fontSize', false, e.target.value); contentRef.current?.focus(); };
  const handleBoldClick        = () => { document.execCommand('bold', false, null); contentRef.current?.focus(); };
  const handleUnderlineClick   = () => { document.execCommand('underline', false, null); contentRef.current?.focus(); };
  const handleItalicClick      = () => { document.execCommand('italic', false, null); contentRef.current?.focus(); };

  const handleLinkInsertion = () => setIsLinkModalOpen(true);

  function insertLinkAtCursorPosition({ displayText, targetUrl }) {
    const safeText = (displayText || targetUrl || 'link').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const safeHref = (targetUrl || '#').replace(/"/g,'&quot;');
    insertHtmlAtCursor(`<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeText}</a>`);
  }

  function insertImageAtCursorPosition({ displayText, imageUrl, key }) {
    insertHtmlAtCursor(
      `<img class="inserted-image" src="${imageUrl}" alt="${(displayText||'').replace(/"/g,'&quot;')}"
            ${key ? `data-asset-key="${key}"` : ''} style="max-width:25%;height:auto;" />`
    );
  }

  function insertFileAtCursorPosition({ displayText, fileUrl }) {
    const label = displayText || (fileUrl?.split('/').pop() ?? 'file');
    insertHtmlAtCursor(
      `<a class="inserted-file" href="${fileUrl}" download="${label.replace(/"/g,'&quot;')}"
          target="_blank" rel="noopener noreferrer">${label}</a>`
    );
  }

  const handleInsertLink = () => {
    insertLinkAtCursorPosition(linkData);
    setIsLinkModalOpen(false);
    contentRef.current?.focus();
  };

  async function uploadToGleSYS(file) {
    // 1) presigned PUT
    const r1 = await api.post('/media/upload-url', { mime: file.type, originalName: file.name });
    const { url, key, publicUrl } = r1.data;
    // 2) PUT directly
    await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    return { key, publicUrl };
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('error', t('errors.fileTooLargeImage'));
      setIsImageModalOpen(false);
      return;
    }
    try {
      const { publicUrl, key } = await uploadToGleSYS(file);
      const url = publicUrl || await getDownloadUrlForPrivate(key);
      setImageData({ displayText: file.name, imageUrl: url, key });
      showToast('success', t('success.uploadReady'));
    } catch {
      showToast('error', t('errors.uploadFailed'));
      setIsImageModalOpen(false);
    }
  };

  const handleInsertImage = () => {
    if (!imageData?.imageUrl) { setIsImageModalOpen(false); return; }
    insertImageAtCursorPosition(imageData);
    setIsImageModalOpen(false);
    contentRef.current?.focus();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast('error', t('errors.fileTooLargeFile'));
      setIsFileModalOpen(false);
      return;
    }
    try {
      const { publicUrl, key } = await uploadToGleSYS(file);
      const url = publicUrl || await getDownloadUrlForPrivate(key);
      setFileData({ displayText: file.name, fileUrl: url });
      showToast('success', t('success.uploadReady'));
    } catch {
      showToast('error', t('errors.uploadFailed'));
      setIsFileModalOpen(false);
    }
  };

  const handleInsertFile = () => {
    if (!fileData?.fileUrl) { setIsFileModalOpen(false); return; }
    insertFileAtCursorPosition(fileData);
    setIsFileModalOpen(false);
    contentRef.current?.focus();
  };

  async function handleSave() {
    const html = contentRef.current?.innerHTML || '';
    try {
      const itemId = item?._id ?? item?.id;
      if (itemId) {
        const { data } = await api.put(`/content-items/${item._id}`, {
          name: item.name,
          content: html,
          input: html,
        });
        showToast('success', data?.msg || t('success.itemUpdated'));
      } else {
        if (!siteContentId) { showToast('error', t('errors.missingSection')); return; }
        const { data } = await api.post('/content-items', {
          contentId: siteContentId,
          name: t('editor.defaultItemName'),
          link: 'untitled',
          content: html,
          input: html,      // write both on create too
        });
        showToast('success', data?.msg || t('success.itemCreated'));
      }
    } catch (e) {
      showToast('error', e?.response?.data?.error || e?.response?.data?.msg || t('errors.saveFailed'));
    }
  }

  return (
    <div className="mx-auto max-w-[80%]">
      <div className="flex items-center gap-2 mb-4 p-2 rounded-b-sm bg-white shadow-lg">
        {/* Back button */}
        <button
          className="px-3 py-2 rounded border hover:bg-gray-50"
          onClick={() => navigate(-1)}
          aria-label={t('general.back') || 'Back'}
        >
          ← {t('general.back') || 'Back'}
        </button>
        <select className="p-2 mr-2 shadow" onChange={handleFontFamilyChange}>
          <option>Arial</option><option>Times New Roman</option><option>Verdana</option>
        </select>
        <select className="p-2 mr-2 shadow" onChange={handleFontSizeChange}>
          <option value="1">12px</option><option value="2">14px</option><option value="3">16px</option>
          <option value="4">18px</option><option value="5">20px</option><option value="6">24px</option>
          <option value="7">32px</option>
        </select>
        <button className={`p-2 mr-2 shadow ${isBold ? 'font-bold' : ''}`} onClick={handleBoldClick}>
          {t('editor.toolbar.bold')}
        </button>
        <button className={`p-2 mr-2 shadow ${isUnderline ? 'underline' : ''}`} onClick={handleUnderlineClick}>
          {t('editor.toolbar.underline')}
        </button>
        <button className={`p-2 mr-2 shadow ${isItalic ? 'italic' : ''}`} onClick={handleItalicClick}>
          {t('editor.toolbar.italic')}
        </button>
        <button className="p-2 mr-2 shadow" onClick={handleLinkInsertion}>
          {t('editor.toolbar.link')}
        </button>
        <button className="p-2 mr-2 shadow" onClick={() => setIsImageModalOpen(true)}>
          {t('editor.toolbar.image')}
        </button>
        <button className="p-2 mr-2 shadow" onClick={() => setIsFileModalOpen(true)}>
          {t('editor.toolbar.file')}
        </button>
        <button className="p-2 shadow" onClick={handleSave}>
          {t('editor.toolbar.save')}
        </button>
      </div>

      <div
        className="border bg-white rounded-sm shadow-lg p-2 outline-none mb-4"
        contentEditable
        ref={contentRef}
      />

      {/* Image modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4">{t('modals.image.title')}</h2>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            <div className="mt-4 flex gap-2">
              <button className="p-2 bg-blue-600 text-white rounded" onClick={handleInsertImage}>
                {t('modals.image.insert')}
              </button>
              <button className="p-2 bg-gray-200 rounded" onClick={() => setIsImageModalOpen(false)}>
                {t('modals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-4 rounded shadow w-[min(520px,92vw)]">
            <h2 className="text-lg font-bold mb-4">{t('modals.link.title')}</h2>
            <label className="block text-sm font-semibold mb-1">{t('modals.link.displayText')}</label>
            <input
              className="border px-2 py-1 w-full mb-2"
              value={linkData.displayText}
              onChange={e=>setLinkData(s=>({...s,displayText:e.target.value}))}
            />
            <label className="block text-sm font-semibold mb-1">{t('modals.link.targetUrl')}</label>
            <input
              className="border px-2 py-1 w-full"
              type="url"
              value={linkData.targetUrl}
              onChange={e=>setLinkData(s=>({...s,targetUrl:e.target.value}))}
            />
            <div className="mt-4 flex gap-2">
              <button className="p-2 bg-blue-600 text-white rounded" onClick={handleInsertLink}>
                {t('modals.link.insert')}
              </button>
              <button className="p-2 bg-gray-200 rounded" onClick={() => setIsLinkModalOpen(false)}>
                {t('modals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File modal */}
      {isFileModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-4 rounded shadow w-[min(520px,92vw)]">
            <h2 className="text-lg font-bold mb-4">{t('modals.file.title')}</h2>
            <label className="block text-sm font-semibold mb-1">{t('modals.file.displayText')}</label>
            <input
              className="border px-2 py-1 w-full mb-2"
              value={fileData.displayText}
              onChange={e=>setFileData(s=>({...s,displayText:e.target.value}))}
            />
            <label className="block text-sm font-semibold mb-1">{t('modals.file.uploadLabel')}</label>
            <input className="border px-2 py-1 w-full" type="file" onChange={handleFileUpload} />
            <div className="mt-4 flex gap-2">
              <button className="p-2 bg-blue-600 text-white rounded" onClick={handleInsertFile}>
                {t('modals.file.insert')}
              </button>
              <button className="p-2 bg-gray-200 rounded" onClick={() => setIsFileModalOpen(false)}>
                {t('modals.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toaster */}
      <Toast notice={toast} onClose={() => setToast(null)} />
    </div>
  );
}
